import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ja'
dayjs.extend(relativeTime)
dayjs.locale('ja')

import { Bosyu } from '@/clients/firebase/firestore/Bosyu'
import { Users } from '@/clients/firebase/firestore/Users'
import { colors } from '@/clients/styles/theme'
import { PinkButton } from '@/clients/components/atoms/Button'
import { getGameTitleDisplayName } from '@/clients/models/types/GameTitle'
import { getRankDisplayName, Rank } from '@/clients/models/types/Rank'
import { Platform, getPlatformDisplayName, getPlatformImageUrl } from '@/clients/models/types/Platform'
import { GameMode, getGameModeDisplayName } from '@/clients/models/types/GameMode'
import { getVoiceChatDisplayName, VoiceChat } from '@/clients/models/types/VoiceChat'
import { VoiceChatPlatform, getVoiceChatPlatformDisplayName } from '@/clients/models/types/VoiceChatPlatform'

type Props = {
  bosyu: Bosyu
  bosyuUser: Users
}

export const BosyuDetail: React.FC<Props> = ({
  bosyu,
  bosyuUser,
}) => {
  return (
    <Container>
      {bosyu && bosyuUser && (
        <>
          <Link
            href={`/users/${bosyu.userId}`}
            style={{
              textDecoration: 'none',
            }}
          >
            <BosyuUserContainer>
              <BosyuUserImageContainer>
                <UserImageContainer>
                  <Image
                    src={bosyuUser.profileImgUrl}
                    alt={`${bosyuUser.name}`}
                    width={40}
                    height={40}
                    unoptimized={true}
                  />
                </UserImageContainer>
                <OnlineBadge isOpen={bosyu.status === 'open'} />
              </BosyuUserImageContainer>
              <BosyuUserName>{bosyuUser.name}さんの募集</BosyuUserName>
              {bosyuUser.aveRatings && (
                <UserRatings>
                  <FontAwesomeIcon
                    width={16}
                    icon={faStar}
                    color={colors.button.quaternary}
                    style={{ marginRight: '4px' }}
                  />
                  {Math.round(bosyuUser.aveRatings * 10) / 10}（
                  {bosyuUser.numOfReviews}件）
                </UserRatings>
              )}
            </BosyuUserContainer>
          </Link>
          {bosyu.bosyuMemo && (
            <BosyuMemoContainer>
              <MemoBoxContainer>
                <MemoBox>
                  <MemoText>{bosyu.bosyuMemo}</MemoText>
                </MemoBox>
              </MemoBoxContainer>
            </BosyuMemoContainer>
          )}
          <BosyuCard>
            <GameTitleContainer>
              <GameTitleImage
                src={`https://storage.googleapis.com/nadare-production-static-images/static/images/gameTitles/icn_${bosyu.bosyuGameTitle}.png`}
              />
              <GameTitleText>
                {getGameTitleDisplayName(bosyu.bosyuGameTitle)}
              </GameTitleText>
              <BosyuCreateAtText suppressHydrationWarning>
                {dayjs(bosyu.createAt.seconds * 1000).fromNow()}
              </BosyuCreateAtText>
            </GameTitleContainer>
            <OgImageContainer>
              <img src={bosyu.ogpImageUrl} loading={'lazy'}/>
            </OgImageContainer>
            <BosyuElement>
              <BosyuLabel>募集人数</BosyuLabel>
              <BosyuConditionLabel>
                <PrimaryTextUnderLine>
                  あと{bosyu.numOfRecruitingUsers}人
                </PrimaryTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>今集まっている人数</BosyuLabel>
              <BosyuConditionLabel>
                <PrimaryTextUnderLine>
                  {bosyu.numOfCurrentRecruitedUsers}人
                </PrimaryTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>プラットフォーム</BosyuLabel>
              <SecondaryTextUnderLine>
                <PlatformsContainer>
                  {bosyu.bosyuPlatforms.map((platform, index) => {
                    return (
                      <Padding key={index}>
                        <Image
                          key={index}
                          src={getPlatformImageUrl(platform as Platform)}
                          alt={getPlatformDisplayName(platform as Platform)}
                          height={18}
                          width={18}
                          unoptimized={true}
                          style={{
                            width: 18,
                            height: 18,
                            objectFit: 'contain',
                          }}
                        />
                      </Padding>
                    )
                  })}
                </PlatformsContainer>
              </SecondaryTextUnderLine>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>募集するボイスチャット</BosyuLabel>
              <BosyuConditionLabel>
                <PurpleTextUnderLine>
                  {bosyu.bosyuVoiceChats && bosyu.bosyuVoiceChats[0] ?
                    getVoiceChatPlatformDisplayName(bosyu.bosyuVoiceChats[0] as VoiceChatPlatform) :
                    'なし'
                  }
                </PurpleTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>募集する通話スタイル</BosyuLabel>
              <BosyuConditionLabel>
                <PurpleTextUnderLine>
                  {getVoiceChatDisplayName(bosyu.bosyuVC as VoiceChat)}
                </PurpleTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>募集しているゲームモード</BosyuLabel>
              <BosyuConditionLabel>
                <SecondaryTextUnderLine>
                  {getGameModeDisplayName(
                    bosyu.bosyuGameModes[0] as GameMode
                  )}
                </SecondaryTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>募集しているランク帯</BosyuLabel>
              <BosyuConditionLabel>
                <SecondaryTextUnderLine>
                  {getRankDisplayName(bosyu.bosyuRanks[0] as Rank)}
                </SecondaryTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
            <BosyuElement>
              <BosyuLabel>ボイスチャット</BosyuLabel>
              <BosyuConditionLabel>
                <SecondaryTextUnderLine>
                  {getVoiceChatDisplayName(bosyu.bosyuVC as VoiceChat)}
                </SecondaryTextUnderLine>
              </BosyuConditionLabel>
            </BosyuElement>
          </BosyuCard>
          <BosyuActionContainer>
            <Link
              href={`https://gamee.gg`}
              style={{
                textDecoration: 'none',
              }}
            >
              <ButtonDescriptionText>
                \ Gameeのアプリ版に移動します /
              </ButtonDescriptionText>
              <LoginButton>ログインして募集に参加する</LoginButton>
            </Link>
          </BosyuActionContainer>
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  color: ${colors.bg.primary};
`

const BosyuCard = styled.div`
  margin: auto;
  margin-top: 20px;
  padding: 12px 20px;
  width: 320px;
  border-radius: 12px;
  box-shadow: 2px 2px 20px rgb(0 0 0 / 30%);
  background: #fff;
`

const GameTitleContainer = styled.div`
  display: flex;
`

const GameTitleImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`

const GameTitleText = styled.div`
  margin: auto;
  margin-left: 8px;
  font-size: 13px;
  font-weight: bold;
  color: ${colors.text.primary};
`

const OgImageContainer = styled.div`
  margin-top: 12px;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
`

const OnlineBadge = styled.div<{
  isOpen: boolean
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: ${({ isOpen }) => (isOpen ? '#34c759' : colors.bg.tertiary)};
  border: 2px solid #fff;
  border-radius: 50%;
`

const BosyuUserContainer = styled.div`
  padding-top: 20px;
  margin: 0 auto;
  width: 100%;
  max-width: 320px;
  display: flex;
  align-items: center;
`

const BosyuUserImageContainer = styled.div`
  position: relative;
  border-radius: 50%;
  width: 40px;
  height: 40px;
`

const UserImageContainer = styled.div`
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  width: 40px;
  height: 40px;
  border: 3px solid #fff;
  border-radius: 50%;
`

const BosyuUserName = styled.div`
  margin-left: 12px;
  font-size: 18px;
  font-weight: bold;
  color: ${colors.text.primary};
`

const UserRatings = styled.div`
  margin-left: 8px;
  display: flex;
  font-weight: bold;
  color: ${colors.text.primary};
  flex: 2;
  font-size: 12px;
`

const BosyuElement = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
`

const BosyuLabel = styled.label`
  margin-bottom: 12px;
  font-weight: bold;
  font-size: 15px;
  color: ${colors.text.primary};
  display: block;
`

const BosyuConditionLabel = styled.label`
  margin-bottom: 12px;
  font-weight: bold;
  font-size: 16px;
  color: ${colors.text.primary};
  text-align: right;
`

const PrimaryTextUnderLine = styled.span`
  background: linear-gradient(transparent 70%, ${colors.border.primary} 70%);
`

const SecondaryTextUnderLine = styled.span`
  background: linear-gradient(transparent 70%, ${colors.border.secondary} 70%);
  height: 20px;
`

const PurpleTextUnderLine = styled.span`
  background: linear-gradient(transparent 70%, ${colors.border.purple} 70%);
  height: 20px;
`

const BosyuActionContainer = styled.div`
  margin-top: 40px;
`

const ButtonDescriptionText = styled.div`
  color: ${colors.text.secondary};
  text-align: center;
  font-size: 12px;
`

const LoginButton = styled(PinkButton)`
  margin: 20px auto;
  font-size: 16px;
  line-height: 40px;
  height: 40px;
  width: 280px;
`

const BosyuCreateAtText = styled.time`
  margin-top: 6px;
  color: ${colors.text.secondary};
  text-align: center;
  font-size: 11px;
  text-align: center;
`

const PlatformsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const Padding = styled.div`
  padding-right: 4px;
  padding-left: 4px;
`

const BosyuMemoContainer = styled.div`
  margin: auto;
  margin-top: 12px;
  width: 320px;
  display: flex;
  flex-direction: column;
`

const MemoBoxContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const MemoBox = styled.div`
  background-color: #fff;
  border-radius: 8px;
  border: 3px solid #b7d7ea;
  padding: 8px;
  margin-right: 12px;
  margin-bottom: 12px;
`

const MemoText = styled.div`
  font-size: 17px;
  line-height: 1.5;
  max-height: 400px;
  overflow: hidden;
  margin: 0;
  color: ${colors.text.primary};
`